/**
 * useSessionLauncher — one door for entering an existing session document.
 *
 * Used by the debrief tridoor and the Dashboard mastery actions: loads the
 * session, seeds the simulation store, and lands in /sim. Keeping a single
 * launcher means every surface that opens a session does it identically.
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSimulationStore } from '../stores';
import { loadSession } from '../services/sessions';

export function useSessionLauncher() {
  const { user } = useAuth();
  const { initSession, setPhase } = useSimulationStore();
  const navigate = useNavigate();
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  // Ref latch — the state above is for button UIs; two clicks in one frame
  // both read its pre-render value, so the guard itself must be synchronous.
  const launchingRef = useRef(false);

  const launchById = async (sessionId: string): Promise<boolean> => {
    if (!user?.uid || launchingRef.current) return false;
    launchingRef.current = true;
    setLaunchingId(sessionId);
    try {
      const session = await loadSession(user.uid, sessionId);
      if (!session) return false;
      const metrics = session.metrics as { currentTurn?: number } | undefined;
      initSession({
        sessionId,
        officerUid: user.uid,
        departmentId: (session.departmentId as string) || null,
        scenario: session.scenario as never,
        worldState: (session.worldState as { public: unknown })?.public as never,
        turns: (session.turnHistory as never[]) || [],
        currentTurnNumber: metrics?.currentTurn || 0,
      });
      setPhase('active');
      navigate('/sim');
      return true;
    } finally {
      launchingRef.current = false;
      setLaunchingId(null);
    }
  };

  return { launchById, launchingId };
}
